import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";

import { ApiError, apiClient, type CatalogImageImportItem } from "../../api/client";

interface DialogProps {
  storeId: string;
  onClose: () => void;
  onComplete: () => void;
}

type ProductField = "name" | "sku" | "asin";

interface ProductValidationError {
  field: ProductField;
  message: string;
}

export function AddProductDialog({ storeId, onClose, onComplete }: DialogProps) {
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [asin, setAsin] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<ProductField | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);
  const skuInput = useRef<HTMLInputElement>(null);
  const asinInput = useRef<HTMLInputElement>(null);
  const imageUrl = useObjectUrl(image);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const validation = validateProduct(sku, asin, name);
    if (validation) {
      setError(validation.message);
      setInvalidField(validation.field);
      ({ name: nameInput, sku: skuInput, asin: asinInput })[validation.field].current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    setInvalidField(null);
    try {
      await apiClient.createCatalogProduct({ storeId, sku, asin, name, image });
      onComplete();
    } catch (caught) {
      setError(errorMessage(caught, "新增商品失败"));
      setSubmitting(false);
    }
  }

  return (
    <ModalFrame title="添加普通商品" onClose={onClose}>
      <form className="catalog-add-form" onSubmit={(event) => void submit(event)}>
        <div className="catalog-add-fields">
          <label>
            <span>品名 *</span>
            <input
              ref={nameInput}
              aria-label="品名 *"
              aria-describedby={invalidField === "name" ? "catalog-add-error" : undefined}
              aria-invalid={invalidField === "name"}
              autoFocus
              data-initial-focus
              maxLength={200}
              onChange={(event) => setName(event.target.value)}
              placeholder="请输入品名"
              value={name}
            />
            <small>{name.length}/200</small>
          </label>
          <label>
            <span>SKU *</span>
            <input
              ref={skuInput}
              aria-label="SKU *"
              aria-describedby={invalidField === "sku" ? "catalog-add-error" : undefined}
              aria-invalid={invalidField === "sku"}
              maxLength={40}
              onChange={(event) => setSku(event.target.value)}
              placeholder="请输入 SKU"
              value={sku}
            />
            <small>{sku.trim().length}/40</small>
          </label>
          <label>
            <span>ASIN</span>
            <input
              ref={asinInput}
              aria-label="ASIN"
              aria-describedby={invalidField === "asin" ? "catalog-add-error" : undefined}
              aria-invalid={invalidField === "asin"}
              maxLength={10}
              onChange={(event) => setAsin(event.target.value)}
              placeholder="10 位字母或数字，可不填"
              value={asin}
            />
            <small>{asin.trim().length}/10</small>
          </label>
        </div>
        <label className="catalog-image-picker">
          <span>商品图片</span>
          <span className="catalog-image-preview">
            {imageUrl ? <img alt="待上传商品预览" src={imageUrl} /> : "无图"}
          </span>
          <input
            accept="image/png,image/jpeg"
            onChange={(event) => setImage(event.target.files?.[0] ?? null)}
            type="file"
          />
          <small>支持 PNG、JPEG，图片会生成缩略图和大图预览。</small>
        </label>
        <DialogFooter
          error={error}
          errorId="catalog-add-error"
          onClose={onClose}
          submitting={submitting}
          submitLabel="保存"
        />
      </form>
    </ModalFrame>
  );
}

export function CsvImportDialog({ storeId, onClose, onComplete }: DialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ApiError["issues"]>([]);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setError("请选择 CSV 文件");
      return;
    }
    setSubmitting(true);
    setError(null);
    setIssues([]);
    try {
      await apiClient.importCatalogCsv(storeId, file);
      onComplete();
    } catch (caught) {
      setError(errorMessage(caught, "CSV 导入失败"));
      if (caught instanceof ApiError) setIssues(caught.issues);
      setSubmitting(false);
    }
  }

  return (
    <ModalFrame title="CSV 批量导入" onClose={onClose} compact>
      <form className="catalog-dialog-form" onSubmit={(event) => void submit(event)}>
        <p>CSV 必须使用 UTF-8 编码，表头固定为 SKU、ASIN、品名。任一行失败时不会写入商品。</p>
        <label className="catalog-file-drop">
          <span>{file ? file.name : "选择 CSV 文件"}</span>
          <input
            accept=".csv,text/csv"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            type="file"
          />
        </label>
        {issues.length > 0 && (
          <ul className="catalog-issue-list" aria-label="CSV 错误行">
            {issues.map((issue) => (
              <li key={`${issue.row}-${issue.code}`}>
                第 {issue.row} 行：{issue.message}
              </li>
            ))}
          </ul>
        )}
        <DialogFooter
          error={error}
          onClose={onClose}
          submitting={submitting}
          submitLabel="开始导入"
        />
      </form>
    </ModalFrame>
  );
}

export function ImageImportDialog({ storeId, onClose, onComplete }: DialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [skus, setSkus] = useState<string[]>([]);
  const [results, setResults] = useState<CatalogImageImportItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function chooseFiles(selected: FileList | null) {
    const next = Array.from(selected ?? []);
    setFiles(next);
    setSkus(next.map((file) => file.name.replace(/\.(png|jpe?g)$/i, "")));
    setResults([]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!files.length || skus.some((sku) => !sku.trim())) {
      setError("请选择图片并为每个文件填写 SKU");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const imported = await apiClient.importCatalogImages(
        storeId,
        files.map((file, index) => ({ file, sku: skus[index] ?? "" })),
      );
      setResults(imported);
      setSubmitting(false);
      if (imported.some((item) => item.status === "success")) onComplete();
    } catch (caught) {
      setError(errorMessage(caught, "商品图片导入失败"));
      setSubmitting(false);
    }
  }

  return (
    <ModalFrame title="导入商品图片" onClose={onClose} compact>
      <form className="catalog-dialog-form" onSubmit={(event) => void submit(event)}>
        <p>选择 PNG 或 JPEG，并明确填写每个文件对应的 SKU。文件之间允许部分成功。</p>
        <label className="catalog-file-drop">
          <span>{files.length ? `已选择 ${files.length} 张图片` : "选择商品图片"}</span>
          <input
            accept="image/png,image/jpeg"
            multiple
            onChange={(event) => chooseFiles(event.target.files)}
            type="file"
          />
        </label>
        {files.length > 0 && (
          <div className="catalog-image-mappings">
            {files.map((file, index) => (
              <label key={`${file.name}-${file.lastModified}`}>
                <span>{file.name}</span>
                <input
                  aria-label={`${file.name} 对应 SKU`}
                  maxLength={40}
                  onChange={(event) => {
                    const next = [...skus];
                    next[index] = event.target.value;
                    setSkus(next);
                  }}
                  placeholder="对应 SKU"
                  value={skus[index] ?? ""}
                />
              </label>
            ))}
          </div>
        )}
        {results.length > 0 && (
          <ul className="catalog-import-results" aria-label="图片导入结果">
            {results.map((item) => (
              <li className={item.status} key={`${item.filename}-${item.sku}`}>
                <strong>{item.filename}</strong>
                <span>{item.status === "success" ? `已关联 ${item.sku}` : item.message}</span>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter
          error={error}
          onClose={onClose}
          submitting={submitting}
          submitLabel="开始导入"
        />
      </form>
    </ModalFrame>
  );
}

export function DeleteProductsDialog({
  storeId,
  productIds,
  onClose,
  onComplete,
}: DialogProps & { productIds: string[] }) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.deleteCatalogProducts(storeId, productIds);
      onComplete();
    } catch (caught) {
      setError(errorMessage(caught, "删除商品失败"));
      setSubmitting(false);
    }
  }

  return (
    <ModalFrame title="删除商品" onClose={onClose} compact>
      <form className="catalog-dialog-form" onSubmit={(event) => void submit(event)}>
        <p>确定删除已选择的 {productIds.length} 个商品吗？重新新增相同 SKU 时会恢复原商品身份。</p>
        <DialogFooter
          danger
          error={error}
          onClose={onClose}
          submitting={submitting}
          submitLabel="确认删除"
        />
      </form>
    </ModalFrame>
  );
}

function ModalFrame({
  title,
  onClose,
  compact = false,
  children,
}: {
  title: string;
  onClose: () => void;
  compact?: boolean;
  children: ReactNode;
}) {
  const modal = useRef<HTMLElement>(null);
  useEffect(() => {
    const container = modal.current;
    if (!container) return;
    const focusable = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]',
        ),
      );
    (container.querySelector<HTMLElement>("[data-initial-focus]") ?? focusable()[0])?.focus();
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const controls = focusable();
      if (!controls.length) return;
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    container.addEventListener("keydown", handleKey);
    return () => container.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="catalog-modal-backdrop" role="presentation">
      <section
        ref={modal}
        aria-labelledby="catalog-dialog-title"
        aria-modal="true"
        className={`catalog-modal ${compact ? "compact" : ""}`}
        role="dialog"
      >
        <header>
          <h2 id="catalog-dialog-title">{title}</h2>
          <button aria-label="关闭对话框" onClick={onClose} type="button">
            关闭
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function DialogFooter({
  error,
  errorId,
  onClose,
  submitting,
  submitLabel,
  danger = false,
}: {
  error: string | null;
  errorId?: string;
  onClose: () => void;
  submitting: boolean;
  submitLabel: string;
  danger?: boolean;
}) {
  return (
    <footer className="catalog-dialog-footer">
      <span aria-live="polite" className="catalog-dialog-error" id={errorId}>
        {error}
      </span>
      <button disabled={submitting} onClick={onClose} type="button">
        取消
      </button>
      <button className={danger ? "danger" : "primary"} disabled={submitting} type="submit">
        {submitting ? "正在处理…" : submitLabel}
      </button>
    </footer>
  );
}

function validateProduct(sku: string, asin: string, name: string): ProductValidationError | null {
  if (!sku.trim() || sku.trim().length > 40) {
    return { field: "sku", message: "SKU 长度必须为 1 至 40 个字符" };
  }
  if (/\p{C}/u.test(sku.trim())) {
    return { field: "sku", message: "SKU 不能包含控制字符" };
  }
  if (asin.trim() && !/^[A-Za-z0-9]{10}$/.test(asin.trim())) {
    return { field: "asin", message: "ASIN 必须由 10 个字母或数字组成" };
  }
  if (!name.trim() || name.trim().length > 200) {
    return { field: "name", message: "品名长度必须为 1 至 200 个字符" };
  }
  if (!/^[\p{L}\p{N}\p{Zs}()（）\-./&,，'’+%:：]+$/u.test(name.trim())) {
    return { field: "name", message: "品名包含不允许的特殊字符" };
  }
  return null;
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

function useObjectUrl(file: File | null): string | null {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  return url;
}
